import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { skippedLabel } from '../utils/healingGate';
import BlockIcon from '@mui/icons-material/Block';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RateReviewIcon from '@mui/icons-material/RateReview';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import ThumbDownAltIcon from '@mui/icons-material/ThumbDownAlt';

const STATUS_CONFIG = {
  pending: {
    label: 'Pending',
    color: '#f59e0b',
    bg: 'rgba(245, 158, 11, 0.15)',
    border: 'rgba(245, 158, 11, 0.3)',
    icon: <HourglassTopIcon sx={{ fontSize: 16 }} />,
  },
  running: {
    label: 'Running',
    color: '#3b82f6',
    bg: 'rgba(59, 130, 246, 0.15)',
    border: 'rgba(59, 130, 246, 0.3)',
    icon: <HourglassTopIcon sx={{ fontSize: 16 }} />,
  },
  awaiting_review: {
    label: 'Awaiting Review',
    color: '#a78bfa',
    bg: 'rgba(167, 139, 250, 0.15)',
    border: 'rgba(167, 139, 250, 0.3)',
    icon: <RateReviewIcon sx={{ fontSize: 16 }} />,
  },
  completed: {
    label: 'Completed',
    color: '#10b981',
    bg: 'rgba(16, 185, 129, 0.15)',
    border: 'rgba(16, 185, 129, 0.3)',
    icon: <CheckCircleIcon sx={{ fontSize: 16 }} />,
  },
  rejected: {
    label: 'Rejected',
    color: '#f97316',
    bg: 'rgba(249, 115, 22, 0.15)',
    border: 'rgba(249, 115, 22, 0.3)',
    icon: <ThumbDownAltIcon sx={{ fontSize: 16 }} />,
  },
  failed: {
    label: 'Failed',
    color: '#ef4444',
    bg: 'rgba(239, 68, 68, 0.15)',
    border: 'rgba(239, 68, 68, 0.3)',
    icon: <ErrorIcon sx={{ fontSize: 16 }} />,
  },
  // DPS-4717: COMPLETED upstream, but the agent never attempted a repair. Not a
  // heal_jobs status: derived by jobDisplayStatus (utils/healOutcome).
  out_of_scope: {
    label: 'Out of scope',
    color: '#94a3b8',
    bg: 'rgba(148, 163, 184, 0.15)',
    border: 'rgba(148, 163, 184, 0.3)',
    icon: <BlockIcon sx={{ fontSize: 16 }} />,
  },
  // DPS-4717: COMPLETED upstream with no PR because the anomaly was not real.
  // Not a heal_jobs status: derived by jobDisplayStatus (utils/healOutcome).
  false_positive: {
    label: 'False positive',
    color: '#a5b4fc',
    bg: 'rgba(165, 180, 252, 0.15)',
    border: 'rgba(165, 180, 252, 0.3)',
    icon: <InfoOutlinedIcon sx={{ fontSize: 16 }} />,
  },
};

/** Badge label for a status, for the Job Tracker status filter too. */
export function statusLabel(status) {
  return STATUS_CONFIG[status]?.label || String(status || '').replace(/_/g, ' ');
}

export default function StatusBadge({ status, title }) {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
  const chip = (
    <Chip
      icon={config.icon}
      label={config.label}
      size="small"
      sx={{
        background: config.bg,
        color: config.color,
        border: `1px solid ${config.border}`,
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: config.color },
      }}
    />
  );
  return title ? <Tooltip title={title}>{chip}</Tooltip> : chip;
}

// A job the relevance gate skipped. Neutral grey: it is not a reviewer rejection.
export function SkippedBadge({ code }) {
  return (
    <Chip
      icon={<SkipNextIcon sx={{ fontSize: 16 }} />}
      label={skippedLabel(code)}
      size="small"
      sx={{
        background: 'rgba(148, 163, 184, 0.15)',
        color: '#94a3b8',
        border: '1px solid rgba(148, 163, 184, 0.3)',
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: '#94a3b8' },
      }}
    />
  );
}

// True when the healing agent's unattended auto-merge path approved this job
// (review_context.auto_approved, set only by the agent, never by a reviewer).
export function isAutoApproved(job) {
  const flag = job?.review_context?.auto_approved;
  return flag === true || (typeof flag === 'string' && flag.trim().toLowerCase() === 'true');
}

// True only after a real auto-merge (review_context.auto_merged, written by the agent
// after it merged the PR). A bot approval alone does not set it: in dry run, or when a
// merge check holds the PR, a person reviews and merges on GitHub.
export function isAutoMerged(job) {
  const flag = job?.review_context?.auto_merged;
  return flag === true || (typeof flag === 'string' && flag.trim().toLowerCase() === 'true');
}

// The bot approved the job and opened the PR, but did not merge it.
export function BotApprovedBadge() {
  return (
    <Chip
      icon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
      label="Bot-approved"
      size="small"
      title="The auto-merge bot approved this job and opened the PR without merging it (dry run, or a merge check held it). A person reviews and merges the PR on GitHub."
      sx={{
        background: 'rgba(148, 163, 184, 0.15)',
        color: '#94a3b8',
        border: '1px solid rgba(148, 163, 184, 0.3)',
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: '#94a3b8' },
      }}
    />
  );
}

// Auto-merged when the agent merged the PR itself, Bot-approved when it only opened it, else nothing.
// True when the job passed every auto-merge pre-check but its attribute is not live yet, so the agent
// left it in Awaiting Review for a person (review_context.auto_merge_candidate, DPS-4660).
export function isAutoMergeCandidate(job) {
  const flag = job?.review_context?.auto_merge_candidate;
  return flag === true || (typeof flag === 'string' && flag.trim().toLowerCase() === 'true');
}

export function AutoMergeCandidateBadge() {
  return (
    <Chip
      icon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
      label="Auto-merge candidate"
      size="small"
      title="Passed every auto-merge check, but this attribute is not live for auto-merge yet, so a person reviews it here as usual."
      sx={{
        background: 'rgba(148, 163, 184, 0.15)',
        color: '#94a3b8',
        border: '1px solid rgba(148, 163, 184, 0.3)',
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: '#94a3b8' },
      }}
    />
  );
}

// Auto-merged when the agent merged the PR itself, Bot-approved when it only opened it,
// Auto-merge candidate when it passed the checks but was left for a person, else nothing.
export function hasAutoApprovalBadge(job) {
  return isAutoMerged(job) || isAutoApproved(job) || isAutoMergeCandidate(job);
}

export function AutoApprovalBadge({ job }) {
  if (isAutoMerged(job)) return <AutoMergedBadge />;
  if (isAutoApproved(job)) return <BotApprovedBadge />;
  if (isAutoMergeCandidate(job)) return <AutoMergeCandidateBadge />;
  return null;
}

// Neutral slate, like SkippedBadge: auto-merging is a mode, not a failure.
export function AutoMergedBadge() {
  return (
    <Chip
      icon={<SmartToyOutlinedIcon sx={{ fontSize: 16 }} />}
      label="Auto-merged"
      size="small"
      title="Approved and merged by the healing agent's auto-merge path, with no human review"
      sx={{
        background: 'rgba(148, 163, 184, 0.15)',
        color: '#94a3b8',
        border: '1px solid rgba(148, 163, 184, 0.3)',
        fontWeight: 600,
        fontSize: '0.75rem',
        '& .MuiChip-icon': { color: '#94a3b8' },
      }}
    />
  );
}
